import classNames from "classnames";
import React, { type InputHTMLAttributes } from "react";
import { Svg } from "../../../../Shared/Assets/Svg/React";
import { CheckboxColor } from "./checkboxColor";
import { CheckboxVariant } from "./checkboxVariant";

interface CheckboxProps {
  additionalClassName?: string;
  extraAttributes?: InputHTMLAttributes<HTMLInputElement>;
  name?: string;
  label?: string;
  disabled?: boolean;
  checked?: boolean;
  color?: CheckboxColor;
  variant?: CheckboxVariant;
  value?: string;
  validationMessage?: string;
  onChange: (checked: boolean) => void;
}

export const Checkbox = ({
  additionalClassName,
  extraAttributes = {},
  name,
  label,
  disabled = false,
  checked = false,
  color = CheckboxColor.Default,
  variant = CheckboxVariant.Element,
  value,
  validationMessage,
  onChange
}: CheckboxProps) => {
  let labelClass = "relative overflow-hidden group";
  let checkmarkClass: string, textClass: string;

  switch (variant) {
    default:
    case CheckboxVariant.Element:
      labelClass = classNames(labelClass, {
        "border rounded-tl-br-sm py-xxs pl-xs pr-sm":
          color === CheckboxColor.Default || color === CheckboxColor.Neutral,
        "py-xxs px-0": color === CheckboxColor.Transparent
      });

      labelClass = classNames(
        labelClass,
        "flex gap-xs cursor-pointer",
        "items-center justify-center",
        "transition-short-in-out",
        "text-t-xs font-t-semi-bold",
        "focus-outline has-[input:focus-visible]:outline",
        {
          [classNames(
            "border-neutral-300 bg-neutral-0",
            "hover:border-turquoise-400 hover:bg-turquoise-200",
            "has-[:focus-visible]:border-turquoise-400 has-[:focus-visible]:bg-turquoise-200",
            "has-[:checked]:border-turquoise-800 has-[:checked]:bg-turquoise-600"
          )]: color === CheckboxColor.Default,
          [classNames(
            "border-neutral-300 bg-transparent",
            "hover:border-neutral-200 hover:bg-transparent",
            "has-[:focus-visible]:border-neutral-200 has-[:focus-visible]:bg-transparent",
            "has-[:checked]:border-dark-blue has-[:checked]:bg-dark-blue"
          )]: color === CheckboxColor.Neutral,
          "border-0 bg-transparent": color === CheckboxColor.Transparent
        }
      );

      checkmarkClass = classNames("transition-short-in-out items-center justify-center", {
        [classNames(
          "text-neutral-200",
          "group-hover:text-t-neutral-700",
          "peer-focus-visible:text-t-neutral-700",
          "group-has-[:focus-visible]:text-t-neutral-700",
          "peer-checked:text-dark-blue",
          "group-has-[:focus-visible]:peer-checked:text-dark-blue"
        )]: color === CheckboxColor.Default,
        [classNames(
          "text-neutral-200",
          "peer-hover:text-t-neutral-700",
          "peer-focus-visible:text-t-neutral-700",
          "group-has-[:focus-visible]:text-t-neutral-700",
          "peer-checked:text-neutral-200 peer-checked:peer-hover:text-neutral-200 group-has-[:focus-visible]:peer-checked:text-neutral-200"
        )]: color === CheckboxColor.Neutral,
        [classNames(
          "border rounded-tl-br-xxs border-neutral-300 bg-transparent text-neutral-300 flex w-md h-md",
          "peer-checked:border-turquoise-400 peer-checked:bg-turquoise-400 peer-checked:text-dark-blue",
          "peer-checked:peer-hover:border-turquoise-400 peer-checked:peer-hover:bg-turquoise-400 peer-checked:peer-hover:text-dark-blue",
          "peer-hover:border-neutral-200 peer-hover:bg-neutral-200 peer-hover:text-t-neutral-400",
          "group-has-[:focus-visible]:border-neutral-200 group-has-[:focus-visible]:bg-neutral-200 group-has-[:focus-visible]:text-t-neutral-400"
        )]: color === CheckboxColor.Transparent
      });

      textClass = classNames("transition-short-in-out", {
        "text-dark-blue": color === CheckboxColor.Default,
        [classNames("text-dark-blue", "group-has-[:checked]:text-neutral-200")]:
          color === CheckboxColor.Neutral,
        [classNames(
          "text-t-neutral-700",
          "group-has-[:checked]:text-dark-blue",
          "group-has-[:hover]:text-dark-blue"
        )]: color === CheckboxColor.Transparent
      });
      break;
    case CheckboxVariant.Block:
      labelClass = classNames(
        labelClass,
        "flex gap-xs border rounded-tl-br-sm py-sm px-md",
        "items-center justify-center min-h-[3rem]",
        "transition-short-in-out cursor-pointer has-[:disabled]:cursor-not-allowed",
        "bg-neutral-0",
        "border-neutral-300 hover:border-turquoise-800 hover:has-[:checked]:border-turquoise-800",
        "focus-outline has-[:focus-visible]:outline",
        "has-[:checked]:border-turquoise-600 has-[:checked]:bg-turquoise-600"
      );

      checkmarkClass = classNames(
        "transition-short-in-out",
        "w-md h-md flex items-center justify-center",
        "border rounded-tl-br-xxs ",
        {
          [classNames(
            "peer-hover:text-t-neutral-200",
            "border-neutral-300 text-neutral-0",
            "peer-checked:bg-turquoise-800 peer-checked:border-turquoise-800 peer-checked:text-dark-blue",
            "peer-checked:peer-hover:bg-turquoise-800 peer-checked:peer-hover:border-turquoise-800 peer-checked:peer-hover:text-dark-blue"
          )]: !disabled,
          [classNames("border-neutral-100", "bg-neutral-100", "text-t-neutral-200")]:
            disabled
        }
      );

      textClass = classNames(
        "transition-short-in-out",
        "font-t-semi-bold text-t-xs text-dark-blue"
      );
      break;
  }

  // Build input props
  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    type: "checkbox",
    name,
    checked,
    disabled,
    value,
    className: "absolute -left-full peer",
    onChange: e => onChange(e.target.checked),
    ...extraAttributes
  };

  // SVG path
  const symbolPath = disabled ? "/svg/toggle/marker.svg" : "/svg/system/check.svg";

  return (
    <div className={additionalClassName}>
      {/* Need to add padding together with a negative margin here, to be able to use overflow hidden while still showing the focus ring. */}
      <label className={labelClass}>
        <span>
          <input {...inputProps} />
          <span className={checkmarkClass}>
            <Svg href={symbolPath} additionalClass="w-md h-md" />
          </span>
        </span>
        <span className={textClass}>{label}</span>
      </label>
      {validationMessage && (
        <span className="block text-t-sm font-t-semi-bold text-error-200 [&.field-validation-error]:mt-xs">
          {validationMessage}
        </span>
      )}
    </div>
  );
};

export default Checkbox;
